import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borders.radius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing[8]};
  min-width: 340px;
  max-width: 95vw;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const NavButton = styled.button`
  background: ${props => props.theme.colors.backgroundTertiary};
  color: ${props => props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  font-size: ${props => props.theme.typography.fontSize.base};
  cursor: pointer;
  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const DayCell = styled.button<{ $selected?: boolean }>`
  background: ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.backgroundTertiary};
  color: ${props => props.$selected ? props.theme.colors.textPrimary : props.theme.colors.textSecondary};
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[3]};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: background 0.2s;
  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const WeekLabel = styled.div`
  text-align: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[4]};
`;

const Button = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${props => props.theme.colors.primaryHover};
  }
`;

const months = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];
const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (date: Date) => void;
}

const CalendarModal: React.FC<CalendarModalProps> = ({ isOpen, onClose, onSelect }) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());

  // Gera os dias do mês atual
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay();

  const days: (Date | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <MonthNav>
            <NavButton onClick={() => {
              if (month === 0) {
                setMonth(11);
                setYear(year - 1);
              } else {
                setMonth(month - 1);
              }
            }}>{'<'}</NavButton>
            <span>{months[month]} {year}</span>
            <NavButton onClick={() => {
              if (month === 11) {
                setMonth(0);
                setYear(year + 1);
              } else {
                setMonth(month + 1);
              }
            }}>{'>'}</NavButton>
          </MonthNav>
        </ModalHeader>
        <CalendarGrid>
          {weekDays.map(day => <WeekLabel key={day}>{day}</WeekLabel>)}
          {days.map((date, idx) =>
            date ? (
              <DayCell
                key={date.toISOString()}
                $selected={!!selectedDate && date.toDateString() === selectedDate.toDateString()}
                onClick={() => setSelectedDate(date)}
              >
                {date.getDate()}
              </DayCell>
            ) : (
              <div key={idx} />
            )
          )}
        </CalendarGrid>
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSelect(selectedDate)}>Selecionar</Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default CalendarModal;

